import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TYPE_INFO = {
  table: { label: "Table Reservation", venues: ["soho", "battersea", "difc"], minSize: 1, maxSize: 12, defaultTime: "19:30" },
  linner: { label: "Mahjong Linner Club", venues: ["soho", "battersea"], minSize: 2, maxSize: 4, defaultTime: "15:00" },
  private_dining: { label: "Private Dining", venues: ["soho", "battersea", "difc"], minSize: 10, maxSize: 40, defaultTime: "19:00" },
  event: { label: "Event / Experience", venues: ["soho", "battersea"], minSize: 1, maxSize: 8, defaultTime: "20:00" },
};

const DEPOSIT_TYPES = new Set(["private_dining", "linner"]);
const fmtMoney = (a, c = "gbp") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: (c || "gbp").toUpperCase() }).format(a);

// 30-min slots from 12:00 to 22:30
const TIME_SLOTS = Array.from({ length: 22 }, (_, i) => {
  const m = 12 * 60 + i * 30;
  const h = String(Math.floor(m / 60)).padStart(2, "0");
  const mm = String(m % 60).padStart(2, "0");
  return `${h}:${mm}`;
});

function nDaysFromNow(n) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + n);
  return d;
}

export default function BookingDialog({ open, type, onClose, onDone }) {
  const info = type ? TYPE_INFO[type] : null;
  const [venue, setVenue] = useState("soho");
  const [partySize, setPartySize] = useState(2);
  const [date, setDate] = useState(nDaysFromNow(2));
  const [time, setTime] = useState("19:30");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [deposit, setDeposit] = useState(null);
  const [calOpen, setCalOpen] = useState(false);

  useEffect(() => {
    if (!info) return;
    setVenue(info.venues[0]);
    setPartySize(Math.max(2, info.minSize));
    setTime(info.defaultTime);
    setDate(nDaysFromNow(2));
    setNotes("");
    setDeposit(null);
    if (DEPOSIT_TYPES.has(type)) {
      api.get(`/checkout/deposit-info/${type}`)
        .then(({ data }) => data?.required && setDeposit(data))
        .catch(() => {});
    }
  }, [type]); // eslint-disable-line

  if (!open || !info) return null;

  const requiresDeposit = DEPOSIT_TYPES.has(type) && deposit?.required;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const isoDate = format(date, "yyyy-MM-dd");
      if (requiresDeposit) {
        const { data } = await api.post("/checkout/booking-deposit", {
          booking_type: type,
          venue,
          party_size: Number(partySize),
          date: isoDate,
          time,
          notes,
          origin_url: window.location.origin,
        });
        sessionStorage.setItem("dr_pending_session", data.session_id);
        window.location.href = data.url;
        return;
      }
      const { data } = await api.post("/bookings", {
        booking_type: type,
        venue,
        party_size: Number(partySize),
        date: isoDate,
        time,
        notes,
      });
      onDone(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Booking failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dr-modal" onClick={onClose} data-testid="booking-dialog">
      <div className="dr-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="dr-modal-eyebrow">RESERVE · {type.toUpperCase().replace("_", " ")}</div>
        <h2 className="dr-modal-title">{info.label}</h2>
        <div className="dr-modal-rule" />

        {requiresDeposit && (
          <div className="dr-deposit-banner" data-testid="deposit-banner">
            <span className="deposit-tag">DEPOSIT</span>
            <span>
              {fmtMoney(deposit.amount, deposit.currency)} held to confirm — fully refundable up to 24h before.
            </span>
          </div>
        )}

        <form onSubmit={submit} className="dr-form">
          {info.venues.length > 1 && (
            <div className="dr-field">
              <label>VENUE</label>
              <div className="dr-segmented">
                {info.venues.map(v => (
                  <button
                    type="button"
                    key={v}
                    data-testid={`venue-${v}-btn`}
                    className={`dr-seg ${venue === v ? "active" : ""}`}
                    onClick={() => setVenue(v)}
                  >
                    {v.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="dr-row-2">
            <div className="dr-field">
              <label>DATE</label>
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="dr-fakeinput"
                    data-testid="booking-date-trigger"
                  >
                    {format(date, "EEE d LLL yyyy")}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="dr-pop" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => { if (d) { setDate(d); setCalOpen(false); } }}
                    fromDate={nDaysFromNow(0)}
                    toDate={nDaysFromNow(60)}
                    initialFocus
                    data-testid="booking-calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="dr-field">
              <label>TIME</label>
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger className="dr-fakeinput" data-testid="booking-time-trigger">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="dr-time-content">
                  {TIME_SLOTS.map(t => (
                    <SelectItem key={t} value={t} data-testid={`time-${t}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="dr-field">
            <label>PARTY SIZE — {partySize}</label>
            <input
              data-testid="booking-party-input"
              type="range"
              min={info.minSize}
              max={info.maxSize}
              value={partySize}
              onChange={(e) => setPartySize(e.target.value)}
            />
            <div className="dr-range-meta">
              <span>{info.minSize}</span>
              <span>{info.maxSize}</span>
            </div>
          </div>

          <div className="dr-field">
            <label>NOTES <span style={{ opacity: 0.5 }}>(OPTIONAL)</span></label>
            <textarea
              data-testid="booking-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Allergies, special requests…"
            />
          </div>

          <div className="dr-actions">
            <button
              data-testid="booking-cancel-btn"
              type="button"
              className="dr-btn-ghost"
              onClick={onClose}
            >
              CANCEL
            </button>
            <button
              data-testid="booking-confirm-btn"
              type="submit"
              className="dr-btn-primary"
              disabled={busy}
            >
              {busy
                ? (requiresDeposit ? "REDIRECTING…" : "RESERVING…")
                : (requiresDeposit ? `PAY ${fmtMoney(deposit.amount, deposit.currency)} DEPOSIT` : "CONFIRM RESERVATION")
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
